import api from "../../api/api";
import { V2_BASE_URL } from "../../api/bases";
import { getPracticeId } from "../../config/practice";

/*
 * Form Wizard staff API (v2 gateway, /v1/{practiceid}/formwiz/*).
 *
 * Every call is pinned to v2: the routes exist on v2 only, so they never go
 * through the v1/v2 routing rules (`_gw` set, see api/api.js) and never fall
 * back to v1. api.js attaches the staff token and returns the response body,
 * {status, message?, data?}; `unwrap` hands back `data`.
 *
 * A 404 from any of these means the server is not deployed yet or the practice
 * is not enabled (FORMWIZ_PRACTICES): callers hide the feature quietly.
 */
const V2 = { baseURL: V2_BASE_URL, _gw: "v2-pinned" };
const MULTIPART = { ...V2, headers: { "Content-Type": "multipart/form-data" } };

const base = () => `/v1/${getPracticeId()}/formwiz`;
const enc = (s) => encodeURIComponent(String(s));
const withParams = (params) => {
  const clean = {};
  Object.entries(params || {}).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== "") clean[k] = v;
  });
  return Object.keys(clean).length ? { ...V2, params: clean } : V2;
};

const isBlob = (v) => typeof Blob !== "undefined" && v instanceof Blob;
export const unwrap = (res) =>
  res && typeof res === "object" && !Array.isArray(res) && !isBlob(res) && "data" in res ? res.data : res;

export const formwizApi = {
  catalog: (all) => api.get(`${base()}/catalog`, withParams({ all: all ? 1 : undefined })).then(unwrap),
  def: (formId, version) => api.get(`${base()}/defs/${enc(formId)}/${enc(version)}`, V2).then(unwrap),
  config: () => api.get(`${base()}/config`, V2).then(unwrap),
  saveConfig: (body) => api.put(`${base()}/config`, body, V2).then(unwrap),
  saveFormConfig: (formId, body) => api.put(`${base()}/config/forms/${enc(formId)}`, body, V2).then(unwrap),

  packets: (all) => api.get(`${base()}/packets`, withParams({ all: all ? 1 : undefined })).then(unwrap),
  createPacket: (body) => api.post(`${base()}/packets`, body, V2).then(unwrap),
  updatePacket: (id, body) => api.put(`${base()}/packets/${enc(id)}`, body, V2).then(unwrap),
  deletePacket: (id) => api.delete(`${base()}/packets/${enc(id)}`, V2).then(unwrap),

  createRequest: (body) => api.post(`${base()}/requests`, body, V2).then(unwrap),
  listRequests: (params) => api.get(`${base()}/requests`, withParams(params)).then(unwrap),
  getRequest: (id) => api.get(`${base()}/requests/${enc(id)}`, V2).then(unwrap),
  resend: (id, body) => api.post(`${base()}/requests/${enc(id)}/resend`, body || {}, V2).then(unwrap),
  cancel: (id) => api.post(`${base()}/requests/${enc(id)}/cancel`, {}, V2).then(unwrap),
  kioskSession: (id) => api.post(`${base()}/requests/${enc(id)}/kiosk-session`, {}, V2).then(unwrap),

  inbox: (params) => api.get(`${base()}/inbox`, withParams(params)).then(unwrap),
  inboxCount: () => api.get(`${base()}/inbox/count`, V2).then(unwrap),
  submission: (id) => api.get(`${base()}/submissions/${enc(id)}`, V2).then(unwrap),
  // The stored PDF as a Blob (api.js returns response.data, the blob itself).
  submissionPdf: (id) => api.get(`${base()}/submissions/${enc(id)}/pdf`, { ...V2, responseType: "blob" }),
  // revision: the staff answers' revision the PDF was rendered from (staff
  // forms / staff sections; the server answers 409 stale_revision when it no
  // longer matches). Left out when not a number.
  uploadPdf: (id, blob, revision) => {
    const fd = new FormData();
    fd.append("pdf", blob, `submission-${id}.pdf`);
    if (typeof revision === "number" && Number.isFinite(revision)) fd.append("revision", String(revision));
    return api.put(`${base()}/submissions/${enc(id)}/pdf`, fd, MULTIPART).then(unwrap);
  },
  review: (id, note) => api.post(`${base()}/submissions/${enc(id)}/review`, note ? { note } : {}, V2).then(unwrap),
  sendToEmr: (id) => api.post(`${base()}/submissions/${enc(id)}/send-to-emr`, {}, V2).then(unwrap),

  patientForms: (patientid) => api.get(`${base()}/patients/${enc(patientid)}/forms`, V2).then(unwrap),
  // Where a text / email would go (masked), decided by the server exactly as
  // POST /requests decides it: {sms|email: {available, destination_masked}, dob_on_file}.
  patientContact: (patientid) => api.get(`${base()}/patients/${enc(patientid)}/contact`, V2).then(unwrap),

  // Patient lookup for the standalone wizard: v1 dssearch, as FormsStation
  // uses it (normal routing; not a formwiz route).
  searchPatients: (searchtxt) => api.post(`/v1/${getPracticeId()}/autocomplete/dssearch`, { searchtxt }).then(unwrap),
};

// "Find in Athena" (v2 only): Athena FHIR patient search for a patient care doesn't
// have yet, and the import that adds them to care. search body: {lastname, firstname?, dob}
// or {patientid}; data {results: [{patientid, firstname, lastname, dob, has_phone,
// has_mobile, in_care}], skipped, hidden}. import data: the patient + created, warning?
// ("no_mobile" | "no_phone").
export const athenaPatientsApi = {
  search: (body) => api.post(`/v1/${getPracticeId()}/patients/athena-search`, body, V2).then(unwrap),
  importPatient: (patientid) =>
    api.post(`/v1/${getPracticeId()}/patients/athena-import`, { patientid: Number(patientid) }, V2).then(unwrap),
};

// GET /config, shared by the pieces that only need the practice's display
// name (kiosk hand-off from the chart panel). Cleared after a failure and
// whenever settings are saved.
let configPromise = null;
export function getConfigCached() {
  if (!configPromise) {
    configPromise = formwizApi.config().catch((e) => {
      configPromise = null;
      throw e;
    });
  }
  return configPromise;
}
export function clearConfigCache() {
  configPromise = null;
}

export default formwizApi;
